import { LocalDate } from "@js-joda/core";
import h from "trader-hyperscript";
import { rpc } from "trader.ts/network";
import { Form, constantField, dateBox, textBox } from "trader.ts/ui.forms";
import { lessongroupid, periodid } from "../../../sql/classy/types";
import * as style from "../../../styles/style.css.classes";
import { makeArrayField, oneLineArrayRender } from "../../client/arrayfield";
import { dateBoxNullable } from "../../client/dateboxnullable";
import { renderSaveButton } from "../../client/savebutton";
import { savePeriodSpec } from "../../routes";
import * as routes from "../../routes";
import { mandatoryValidation } from "../../lib/validate";
import { ddMMyyyy } from "../../render";

export function render({
  period,
}: {
  period: {
    id: null | periodid;
    firstday: LocalDate;
    lastday: LocalDate;
    earliestpublicplanningdate: LocalDate;
    openpublicenrollments: LocalDate | null;
    closepublicenrollments: LocalDate;
    holidays: {
      firstday: LocalDate;
      lastday: LocalDate;
      description: string;
    }[];
  };
}): HTMLElement | HTMLElement[] {
  const form = new Form({})
    .addField("id", [], () => constantField(period.id))
    .addField("firstday", [], () =>
      dateBox({ initialVal: period.firstday, label: "Eerste lesdag" }),
    )
    .addField("lastday", [], () =>
      dateBox({ initialVal: period.lastday, label: "Laatste lesdag" }),
    )
    .addField("openpublicenrollments", [], () =>
      dateBoxNullable({
        initialVal: period.openpublicenrollments,
        label: "Online inschrijvingen openen vanaf",
      }),
    )
    .addField("earliestpublicplanningdate", [], () =>
      dateBox({
        initialVal: period.earliestpublicplanningdate,
        label: "Eerste lesdag voor online inschrijvingen",
      }),
    )
    .addField("closepublicenrollments", [], () =>
      dateBox({
        initialVal: period.closepublicenrollments,
        label: "Online inschrijvingen sluiten op",
      }),
    )
    .addField("holidays", [], () =>
      makeArrayField({
        initialVal: period.holidays.orderBy((h) => h.firstday.toEpochDay()),
        render: oneLineArrayRender((t) => t.description === ""),
        makeSubForm: async (t: {
          firstday: LocalDate;
          lastday: LocalDate;
          description: string;
        }) => {
          return new Form({})
            .addField("description", [], () =>
              textBox({
                initialVal: t.description,
                validations: [mandatoryValidation],
              }),
            )
            .addField("firstday", [], () => dateBox({ initialVal: t.firstday }))
            .addField("lastday", [], () => dateBox({ initialVal: t.lastday }))
            .build();
        },
        makeNew: () => {
          return {
            description: "",
            firstday: LocalDate.now(),
            lastday: LocalDate.now(),
          };
        },
      }),
    )
    .build({
      render: (fields) => (
        <div>
          <section className={style.equal_columns}>
            {fields.firstday()}
            {fields.lastday()}
          </section>

          {fields.openpublicenrollments()}
          <section
            className={style.helptext}
            style="position: relative; top: -8px;"
          >
            {"Laat je deze leeg, dan openen de online inschrijvingen meteen na de laatste dag van het vorige schooljaar"}
          </section>

          {fields.earliestpublicplanningdate()}
          <section
            className={style.helptext}
            style="position: relative; top: -8px;"
          >
            {"Deze datum kan je laten afwijken van de eerste lesdag als je lessen starten op 1 september maar lessen via online inschrijvingen starten pas op 8 september"}
          </section>

          {fields.closepublicenrollments()}
          <section
            className={style.helptext}
            style="position: relative; top: -8px;"
          >
            {"Dit is de laatste dag waarop studenten online kunnen inschrijven voor dit schooljaar"}
          </section>

          <hr />
          <br />

          <label>{"Vakanties"}</label>
          <section>{fields.holidays()}</section>
        </div>
      ),
    });
  return (
    <div>
      {form.render()}
      <div style="text-align: right">
        {renderSaveButton({
          formS: form.s,
          save: (values) =>
            rpc(savePeriodSpec, undefined, values).then((res) => {
              if (res.tag === "success") {
                if (
                  res.case === "updated_period" &&
                  res.updatedLessongroups.length > 0
                ) {
                  return {
                    tag: "success" as const,
                    text: renderPeriodUpdateResult(res),
                  };
                } else {
                  setTimeout(
                    () =>
                      (document.location = routes.config_periodsRoute.link({
                        periodid: res.id,
                      })),
                    1500,
                  );
                }
              }
              return res;
            }),
          timeout: 1000 * 60 * 60,
          reload: false,
        })}
      </div>
    </div>
  );
}
function renderPeriodUpdateResult(res: {
  tag: "success";
  id: periodid;
  case: "updated_period";
  updatedLessongroups: {
    lessongroupid: lessongroupid;
    deleted: {
      date: LocalDate;
    }[];
    inserted: { date: LocalDate }[];
  }[];
}): HTMLElement {
  return (
    <div style="text-align: left;">
      {res.updatedLessongroups.map((lg) => (
        <div>
          <div>
            <a
              target="_blank"
              href={routes.lessongroup_lessondataRoute.link({
                lessongroupid: lg.lessongroupid,
                monthsinfuture: undefined,
                selecteddate: undefined,
              })}
            >
              {"Lessenreeks" + " " + lg.lessongroupid}
            </a>
          </div>
          <div>
            {"Lesdata aangepast" +
              ": " +
              lg.deleted
                .map(
                  (d, i) =>
                    d.date.format(ddMMyyyy) +
                      " -> " +
                      lg.inserted[i]?.date.format(ddMMyyyy) || "",
                )
                .join(", ")}
          </div>
        </div>
      ))}
    </div>
  );
}
